/** A QR rectangle in template millimetres or in the final server raster dots. */
export interface QrArea { x: number; y: number; w: number; h: number; unit?: 'mm' | 'px' }

/** Invert the visible RGB channels without touching transparency. */
export function invertRgba(pixels: Uint8ClampedArray): Uint8ClampedArray {
  for (let offset = 0; offset < pixels.length; offset += 4) {
    pixels[offset] = 255 - (pixels[offset] ?? 0);
    pixels[offset + 1] = 255 - (pixels[offset + 1] ?? 0);
    pixels[offset + 2] = 255 - (pixels[offset + 2] ?? 0);
  }
  return pixels;
}

/**
 * Put QR blocks back to ordinary black-on-white after inverting a label.
 *
 * A scanner expects a dark QR on a light quiet zone.  Repainting the source
 * rectangle alone leaves black inverted label pixels touching its edge, so a
 * two-pixel white frame is restored as well.
 */
export function restoreQrAreas(
  inverted: Uint8ClampedArray,
  normal: Uint8ClampedArray,
  width: number,
  height: number,
  areas: readonly QrArea[],
  pixelsPerMm: number,
): Uint8ClampedArray {
  const frame = 2;
  const writeWhite = (x: number, y: number) => {
    const offset = (y * width + x) * 4;
    inverted[offset] = 255;
    inverted[offset + 1] = 255;
    inverted[offset + 2] = 255;
    inverted[offset + 3] = 255;
  };
  const restore = (x: number, y: number) => {
    const offset = (y * width + x) * 4;
    inverted[offset] = normal[offset] ?? 255;
    inverted[offset + 1] = normal[offset + 1] ?? 255;
    inverted[offset + 2] = normal[offset + 2] ?? 255;
    inverted[offset + 3] = normal[offset + 3] ?? 255;
  };

  for (const area of areas) {
    const scale = area.unit === 'px' ? 1 : pixelsPerMm;
    const left = Math.max(0, Math.floor(area.x * scale));
    const top = Math.max(0, Math.floor(area.y * scale));
    const right = Math.min(width, Math.ceil((area.x + area.w) * scale));
    const bottom = Math.min(height, Math.ceil((area.y + area.h) * scale));
    if (left >= right || top >= bottom) continue;

    for (let y = Math.max(0, top - frame); y < Math.min(height, bottom + frame); y += 1) {
      for (let x = Math.max(0, left - frame); x < Math.min(width, right + frame); x += 1) writeWhite(x, y);
    }
    for (let y = top; y < bottom; y += 1) {
      for (let x = left; x < right; x += 1) restore(x, y);
    }
  }
  return inverted;
}
